import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { SuccessDialog } from '@/components/organisms/self-checkin/payment/success-dialog'
import { ErrorDialog } from '@/components/organisms/self-checkin/payment/error-dialog'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import * as vescajs from '@/apis/vescajs'
import { LanguageType } from '@/i18n'
import { useGuestAppUrl } from '@/hooks/use-guest-app-url'
import { useQuery } from '@/hooks/use-query'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { reportError } from '@/components/apps/error-boundary'
import { debugState } from '@/apis/aipass'
import { ProgressDialog } from '@/components/organisms/self-checkin/payment/progress-dialog'
import { LoaderContextCreator } from '@/contexts/loader'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

type TerminalStatus = 'progress' | 'success' | 'error'

export const SelfCheckinPaymentTerminalWithCard: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const query = useQuery()
  const { state, doCheckIn, stateErrorHandling } = useSelfCheckInState()
  const [receiveTerminalStatus, setReceiveTerminalStatus] = useState<TerminalStatus>()
  const { mypageUrl, homePageUrl } = useGuestAppUrl({
    hotelId: state.hotelId,
    plugin: state.setting.plugin,
  })
  const { setIsLoading } = useContext(LoaderContextCreator())

  // note ブラウザバックなどで決済ページを復元させないために、履歴にスタックさせないようにreplaceをもちいる
  const goBack = () => {
    setIsLoading(false)
    history.replace({ pathname: '/self-checkin/payment' })
  }

  const onSuccess = async (event: vescajs.OutputCompleteEvent) => {
    setIsLoading(false)
    setReceiveTerminalStatus('success')
    try {
      await doCheckIn({
        state,
        language: i18n.language,
        guestAppUrl: { mypageUrl, homePageUrl },
        selectedPaymentMethod: 'CREDIT_CARD',
        paymentResult: {
          kind: 'credit',
          deviceType: vescajs.DeviceType.PaidCreditCard,
          cardBrand: event.pmsResult.paymentMethod,
          ...event.pmsResult,
        },
      })
      history.replace({ pathname: '/self-checkin/issue-room-key' })
    } catch (e) {
      reportError(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    }
  }

  useEffect(() => {
    if (!stateErrorHandling(state, 'selectReservation')) {
      return
    }
    const vescaJs = new vescajs.VescaJs({
      hotelId: state.hotelId,
      handler: {
        onCancel: () => {
          setReceiveTerminalStatus(undefined)
          goBack()
        },
        onError: () => {
          setIsLoading(false)
          setReceiveTerminalStatus('error')
          setTimeout(() => goBack(), 5000)
        },
        onProcess: () => {
          setReceiveTerminalStatus('progress')
        },
        onReconnect: () => {
          setIsLoading(true)
        },
        onSuccess,
      },
      paymentTarget: {
        reservationIds: state.selectReservation.selectedReservationIds,
        guestRoomAssignIds: state.selectReservation.selectedReservationAndRooms.flatMap(r => r.guestRoomAssignIds),
      },
    })

    vescaJs.doCreditPay({
      amount: state.confirm!.payment!.usage.totalAmount,
      tax: 0,
      lang: i18n.language as LanguageType,
      unionPay: query.has('unionPay'),
    })

    return () => {
      vescaJs.dispose()
    }
  }, [])

  return (
    <SelfCheckinLayout
      state={state}
      header={{
        goToPreviousPage: () => {
          if (receiveTerminalStatus === 'progress') {
            return
          }
          debugState('card-form', { call: 'ci header' })
          goBack()
        },
        title: t('Payment details'),
      }}
      subtitle={{
        title: t('Insert or touch the card'),
        description: t('To change the payment method, press the red button on the payment terminal.'),
      }}
    >
      <div css={contentStyle}>
        <div className="terminal-and-brand-block">
          <div className="terminal-image-block">
            <img src={require('@/static/images/self-checkin-payment/terminal/image_01.png')} />
            <img src={require('@/static/images/self-checkin-payment/terminal/image_02.png')} />
          </div>
          <div className="brand-image-block">
            <div className="-label">{t('Supported Credits')}</div>
            <div className="-separate"></div>
            <div className="-image">
              <img src={require('@/static/images/self-checkin-payment/terminal/logo_card02.png')} />
            </div>
          </div>
        </div>
      </div>
      {receiveTerminalStatus === 'progress' && <ProgressDialog />}
      {receiveTerminalStatus === 'success' && <SuccessDialog />}
      {receiveTerminalStatus === 'error' && <ErrorDialog />}
    </SelfCheckinLayout>
  )
}

const contentStyle = css(
  {
    '*': { fontFamily: 'Noto Sans JP', color: '#272727' },
    '.terminal-and-brand-block': {
      height: 597,
      backgroundColor: '#ffffff',
      padding: 40,
      '.terminal-image-block': {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 22,
        img: {
          width: '100%',
          height: 419,
          objectFit: 'cover',
        },
      },
      '.brand-image-block': {
        display: 'grid',
        gridTemplateColumns: '182px 1px minmax(0, 1fr)',
        textAlign: 'center',
        alignItems: 'center',
        border: '1px solid #CCCCCC',
        borderRadius: 10,
        width: 794,
        maxWidth: '100%',
        height: 75,
        margin: '32px 0 0 95px',
        '.-label': {
          fontSize: 14,
          fontWeight: 'bold',
          lineHeight: '19px',
          letterSpacing: 0.7,
          color: '#676767',
        },
        '.-separate': {
          height: 43,
          borderRight: '1px solid #CCCCCC',
        },
        '.-image': {
          paddingLeft: 39,
          img: { display: 'block', width: 524, maxWidth: 'none', height: 43, objectFit: 'contain' },
        },
      },
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape11: { '.terminal-and-brand-block': { borderRadius: '10px' } },
    portrait: {
      '.terminal-and-brand-block': {
        height: 502,
        '.terminal-image-block': {
          gap: 18,
          img: { height: 315 },
        },
        '.brand-image-block': {
          width: 730,
          marginLeft: 10,
          '.-image img': { width: 467, height: 38 },
        },
      },
    },
    landscape13: {
      '.terminal-and-brand-block': {
        borderRadius: '10px',
        height: 697,
        '.terminal-image-block': {
          gap: 26,
          img: { height: 490 },
        },
        '.brand-image-block': {
          gridTemplateColumns: '251px 1px minmax(0, 1fr)',
          width: 1149,
          height: 95,
          marginLeft: 0,
          '.-label': { fontSize: 18, lineHeight: '25px', letterSpacing: 0.9 },
          '.-separate': { height: 63 },
          '.-image': {
            paddingLeft: 59,
            img: { width: 778, height: 64 },
          },
        },
      },
    },
  }),
)
